import { AnimatePresence, motion } from 'motion/react';
import { memo, useCallback, useLayoutEffect, useRef, type CSSProperties } from 'react';
import type { PieceImages } from '../assets/sprites.js';
import {
  MARK_BALL,
  RAIL_SLOTS,
  VIEW_ASPECT,
  ballCentre,
  cellBox,
  cellMark,
  dimpleInCell,
  inCell,
  layerBox,
  railBox,
  railRollFrom,
  type KenoMark,
  type KenoPiece,
  type PctBox,
} from './kenoArt.js';

export interface KenoCellView {
  n: number;
  picked: boolean;
  drawn: boolean;
}

/** One drawn ball on the rail, in draw order. */
export interface KenoRailBall {
  n: number;
  /** The number was one of the player's picks. */
  hit: boolean;
}

interface Props {
  /** The pictures, or null while they load: the cells are laid out, unseen, so nothing moves when they arrive. */
  art: PieceImages<KenoPiece> | null;
  cells: KenoCellView[];
  onPick: (n: number) => void;
  disabled: boolean;
  /** Picking is locked outside a reveal (auto-bet running): the open numbers fade back. */
  faded: boolean;
  reduced: boolean;
  /** The numbers drawn so far, in draw order. */
  rail?: KenoRailBall[];
  /** A reveal is running: the bowl churns. */
  drawing?: boolean;
  /** Auto-bet's pace: the rail balls roll in faster, to keep up with the shorter gap. */
  quick?: boolean;
  /** The round settled as a win: the gold balls hop. */
  celebrate?: boolean;
  /** The rail's name for screen readers. */
  railLabel?: string;
}

const at = (b: PctBox): CSSProperties => ({ position: 'absolute', left: `${b.left}%`, top: `${b.top}%`, width: `${b.width}%`, height: `${b.height}%` });
const SHADOW = inCell('ballshadow');
const RING = dimpleInCell();
const CENTRE = ballCentre();
const BOARD = layerBox('board');
const BOWL = layerBox('bowl');
const NUMBER_AT: CSSProperties = { left: `${CENTRE.left}%`, top: `${CENTRE.top}%`, transform: 'translate(-50%, -50%)' };

const NUMBER_INK: Record<KenoMark, string> = {
  open: 'text-[#d3e3ff] [text-shadow:0_1px_2px_rgba(0,0,20,.8)]',
  picked: 'text-white [text-shadow:0_1px_2px_rgba(0,45,90,.7)]',
  hit: 'text-white [text-shadow:0_1px_2px_rgba(100,52,0,.75)]',
  drawn: 'text-white [text-shadow:0_1px_3px_rgba(0,20,70,.95)]',
};

const SPARKS = [
  [-85, -75, 0.95],
  [90, -62, 0.8],
  [0, -112, 1.05],
  [95, 38, 0.65],
  [-95, 32, 0.7],
] as const;

/**
 * The Keno board from its 3D render: the navy tray, a dimple under each of the 40 numbers,
 * and the draw rail along its back where the drawn balls roll in from the bowl, in order.
 * Each number is a real button over its dimple and writes its own digits, so the board keeps
 * the grid's keyboard and screen-reader behaviour and every number is crisp.
 */
export function KenoArtBoard({ art, cells, onPick, disabled, faded, reduced, rail = [], drawing = false, quick = false, celebrate = false, railLabel }: Props) {
  // One pick handler for the board's life, so a cell whose own state did not change skips the
  // re-render each pick and each drawn number would otherwise cost all 40.
  const latest = useRef(onPick);
  useLayoutEffect(() => {
    latest.current = onPick;
  });
  const pick = useCallback((n: number) => latest.current(n), []);
  return (
    <div
      data-keno-art=""
      className={['relative mx-auto w-full [container-type:inline-size] transition-opacity duration-300', art ? '' : 'opacity-0'].join(' ')}
      // Short screens narrow the board, so the readouts above it and the paytable below stay in view.
      style={{ aspectRatio: String(VIEW_ASPECT), maxWidth: `max(320px, calc((100svh - 400px) * ${VIEW_ASPECT.toFixed(4)}))` }}
    >
      {art && <img src={art.board.src} alt="" draggable={false} className="pointer-events-none select-none" style={at(BOARD)} />}
      {art && (
        <img
          src={art.bowl.src}
          alt=""
          draggable={false}
          data-drawing={drawing && !reduced ? 'true' : 'false'}
          className="og-keno-bowl pointer-events-none select-none"
          style={at(BOWL)}
        />
      )}
      <div role="list" aria-label={railLabel} data-keno-rail="" className="pointer-events-none absolute inset-0">
        {rail.slice(0, RAIL_SLOTS).map((b, i) => (
          <RailBall key={`${i}-${b.n}`} i={i} n={b.n} hit={b.hit} art={art} reduced={reduced} quick={quick} celebrate={celebrate && b.hit} />
        ))}
      </div>
      {cells.map((c) => (
        <Cell
          key={c.n}
          n={c.n}
          picked={c.picked}
          drawn={c.drawn}
          art={art}
          onPick={pick}
          disabled={disabled}
          faded={faded}
          reduced={reduced}
          celebrate={celebrate && c.picked && c.drawn}
        />
      ))}
    </div>
  );
}

interface CellProps {
  n: number;
  picked: boolean;
  drawn: boolean;
  art: PieceImages<KenoPiece> | null;
  onPick: (n: number) => void;
  disabled: boolean;
  faded: boolean;
  reduced: boolean;
  celebrate: boolean;
}

/** The number's button: cheap to redraw, which every reveal does to all 40 (they lock). */
// Plain values, not the view object: the board builds new views every reveal tick, and a
// fresh object would redraw all 40 buttons for the one ball that landed.
const Cell = memo(function Cell({ n, picked, drawn, art, onPick, disabled, faded, reduced, celebrate }: CellProps) {
  const mark = cellMark(picked, drawn);
  return (
    <button
      type="button"
      data-cell={n}
      data-active={picked ? 'true' : 'false'}
      data-hit={mark === 'hit' ? 'true' : 'false'}
      disabled={disabled}
      onClick={() => onPick(n)}
      className="og-keno-cell group absolute select-none rounded-full border-0 bg-transparent p-0 outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] disabled:cursor-default"
      style={at(cellBox(n))}
    >
      <span aria-hidden className="og-keno-ring pointer-events-none" data-ring={mark} style={at(RING)} />
      <CellPicture n={n} mark={mark} art={art} faded={faded && mark === 'open'} reduced={reduced} celebrate={celebrate} />
    </button>
  );
});

interface PictureProps {
  n: number;
  mark: KenoMark;
  art: PieceImages<KenoPiece> | null;
  faded: boolean;
  reduced: boolean;
  celebrate: boolean;
}

/** The ball and its number; redrawn only when the number's mark changes, never for its button locking. */
const CellPicture = memo(function CellPicture({ n, mark, art, faded, reduced, celebrate }: PictureProps) {
  const ball = mark === 'open' ? null : MARK_BALL[mark];
  const hit = mark === 'hit';
  return (
    <span
      className={['pointer-events-none absolute inset-0', celebrate && !reduced ? 'og-keno-hop' : ''].join(' ')}
      // A wave across the board, column by column.
      style={celebrate ? { animationDelay: `${((n - 1) % 8) * 60}ms` } : undefined}
    >
      {art && (
        <AnimatePresence initial={false}>
          {hit && (
            <motion.img
              key="glow"
              src={art.glow.src}
              alt=""
              draggable={false}
              className="pointer-events-none max-w-none"
              style={at({ left: -40, top: -40, width: 180, height: 180 })}
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: reduced ? 0.5 : [0, 1, 0.5], scale: reduced ? 1 : [0.4, 1.35, 1] }}
              exit={{ opacity: 0, transition: { duration: 0.2 } }}
              transition={{ duration: 0.65 }}
            />
          )}
        </AnimatePresence>
      )}
      {art && ball && <img src={art.ballshadow.src} alt="" draggable={false} className="pointer-events-none" style={at(SHADOW)} />}
      {art && (
        <AnimatePresence initial={false}>
          {ball && (
            <motion.img
              key={ball}
              src={art[ball].src}
              alt=""
              draggable={false}
              className="og-keno-ball pointer-events-none absolute inset-0 h-full w-full"
              style={{ transformOrigin: '50% 80%' }}
              {...entrance(mark, reduced)}
            />
          )}
        </AnimatePresence>
      )}
      {art && hit && !reduced && <HitBurst art={art} />}
      <span
        className={[
          'og-keno-ball absolute font-extrabold leading-none tabular-nums transition-opacity duration-300',
          '[font-size:clamp(12px,2.9cqw,24px)]',
          NUMBER_INK[mark],
          faded ? 'opacity-50' : '',
        ].join(' ')}
        // `transform` centres it; the hover lift moves it with `translate`, so the two add up.
        style={NUMBER_AT}
      >
        {n}
      </span>
    </span>
  );
});

/** How each ball arrives: a pick drops in with a spring, a drawn bead falls and bounces, a hit pops. */
function entrance(mark: KenoMark, reduced: boolean) {
  const exit = { opacity: 0, transition: { duration: 0.15 } };
  if (reduced) return { initial: { opacity: 0 }, animate: { opacity: 1 }, exit, transition: { duration: 0.15 } };
  if (mark === 'hit') {
    return {
      initial: { opacity: 0, scale: 0.35 },
      animate: { opacity: 1, scale: [0.35, 1.32, 0.94, 1] },
      exit,
      transition: { duration: 0.46, times: [0, 0.45, 0.75, 1], ease: 'easeOut' as const },
    };
  }
  if (mark === 'drawn') {
    return {
      initial: { opacity: 0, y: '-150%', scale: 0.9 },
      animate: { opacity: 1, y: ['-150%', '0%', '-12%', '0%'], scale: [0.9, 1.06, 0.98, 1], scaleY: [1, 0.86, 1.04, 1] },
      exit,
      transition: { duration: 0.42, times: [0, 0.55, 0.78, 1], ease: 'easeOut' as const },
    };
  }
  return {
    initial: { opacity: 0, y: '-55%', scale: 0.7 },
    animate: { opacity: 1, y: '0%', scale: 1 },
    exit,
    transition: { type: 'spring' as const, stiffness: 520, damping: 17 },
  };
}

/** A hit's moment: a ring of gold light rushing out and sparks thrown off the ball. */
function HitBurst({ art }: { art: PieceImages<KenoPiece> }) {
  return (
    <span className="pointer-events-none absolute inset-0">
      <span className="og-keno-shock" style={at(RING)} />
      {SPARKS.map(([dx, dy, s], k) => (
        <motion.img
          key={k}
          src={art.sparkle.src}
          alt=""
          draggable={false}
          className="absolute max-w-none"
          style={{ left: '30%', top: '30%', width: '40%', height: '40%' }}
          initial={{ x: '0%', y: '0%', scale: 0, opacity: 1 }}
          animate={{ x: `${dx}%`, y: `${dy}%`, scale: [0, s, 0], opacity: [1, 1, 0], rotate: 90 }}
          transition={{ duration: 0.65, delay: 0.12 + k * 0.02, ease: 'easeOut' }}
        />
      ))}
    </span>
  );
}

interface RailBallProps {
  i: number;
  n: number;
  hit: boolean;
  art: PieceImages<KenoPiece> | null;
  reduced: boolean;
  quick: boolean;
  celebrate: boolean;
}

/** A drawn ball on the rail: it leaves the bowl and rolls to its slot, its number turning as it goes. */
const RailBall = memo(function RailBall({ i, n, hit, art, reduced, quick, celebrate }: RailBallProps) {
  const from = railRollFrom(i);
  const roll = reduced ? { duration: 0.15 } : { duration: quick ? 0.16 : 0.4, ease: [0.3, 1.1, 0.45, 1] as const };
  const piece: KenoPiece = hit ? 'ball-gold' : 'ball-bead';
  return (
    <div role="listitem" data-rail={i} data-rail-hit={hit ? 'true' : 'false'} style={at(railBox(i))}>
      <span
        className={['absolute inset-0', celebrate && !reduced ? 'og-keno-hop' : ''].join(' ')}
        style={celebrate ? { animationDelay: `${i * 60}ms` } : undefined}
      >
        <motion.span
          className="absolute inset-0"
          initial={reduced ? { opacity: 0 } : { x: `${from.x}%`, y: `${from.y}%`, opacity: 0, scale: 0.5 }}
          animate={{ x: '0%', y: '0%', opacity: 1, scale: 1 }}
          transition={reduced ? roll : { ...roll, opacity: { duration: 0.08 } }}
        >
          {art && hit && (
            <motion.img
              src={art.glow.src}
              alt=""
              draggable={false}
              className="pointer-events-none max-w-none"
              style={at({ left: -45, top: -45, width: 190, height: 190 })}
              initial={{ opacity: 0 }}
              animate={{ opacity: reduced ? 0.55 : [0, 1, 0.55] }}
              transition={{ duration: 0.5, delay: reduced ? 0 : roll.duration }}
            />
          )}
          {art && <img src={art.ballshadow.src} alt="" draggable={false} className="pointer-events-none" style={at(SHADOW)} />}
          {art && <img src={art[piece].src} alt="" draggable={false} className="pointer-events-none absolute inset-0 h-full w-full" />}
          <motion.span
            className={['absolute font-extrabold leading-none tabular-nums', '[font-size:clamp(10px,2.2cqw,18px)]', NUMBER_INK[hit ? 'hit' : 'drawn']].join(' ')}
            // Motion owns this one's transform (the turn), so it centres the number with x and y.
            style={{ left: `${CENTRE.left}%`, top: `${CENTRE.top}%`, x: '-50%', y: '-50%' }}
            initial={reduced ? false : { rotate: -320 }}
            animate={{ rotate: 0 }}
            transition={roll}
          >
            {n}
          </motion.span>
        </motion.span>
      </span>
    </div>
  );
});
